import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle, Sparkles, ArrowRight, Loader2, AlertCircle, BookOpen, Languages, ScrollText, Map, MessageSquare, Search } from 'lucide-react';
import axios from 'axios';
import Confetti from 'react-confetti';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  const [subscription, setSubscription] = useState(null);
  const [showConfetti, setShowConfetti] = useState(true);
  const [verifying, setVerifying] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    verifyThenLoad();
    const timer = setTimeout(() => setShowConfetti(false), 5000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const verifyThenLoad = async () => {
    const params = new URLSearchParams(window.location.search);
    const provider = params.get('provider');
    const sessionId = params.get('session_id');
    const orderId = params.get('token'); // PayPal appends ?token=<orderId>
    try {
      if (provider === 'stripe' && sessionId) {
        // Poll Stripe verify (idempotent) until paid / terminal.
        for (let i = 0; i < 6; i++) {
          const { data } = await axios.post(
            `${BACKEND_URL}/api/payments/stripe/verify`,
            { session_id: sessionId },
            { withCredentials: true },
          );
          if (data.success && !data.pending) break;
          await sleep(2000);
        }
      } else if (provider === 'paypal' && orderId) {
        await axios.post(
          `${BACKEND_URL}/api/payments/paypal/verify`,
          { order_id: orderId },
          { withCredentials: true },
        );
      }
    } catch (e) {
      // fall through — the webhook may still credit it; we re-check below.
      console.error('Payment verification error:', e);
    }
    await loadSubscription();
    setVerifying(false);
  };

  const loadSubscription = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/payments/subscription`, {
        withCredentials: true,
      });
      setSubscription(data.subscription);
      setFailed(!data.subscription);
      if (data.subscription) {
        // Upgrade completed — clear any abandoned-checkout retry flag.
        try { localStorage.removeItem('lumera_pending_upgrade'); } catch (e) {}
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
      setFailed(true);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/20 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 flex flex-col items-center gap-4">
            <Loader2 className="w-12 h-12 animate-spin text-purple-600" />
            <p className="text-lg font-medium">
              {language === 'es' ? 'Verificando tu pago…' : 'Verifying your payment…'}
            </p>
            <p className="text-sm text-muted-foreground text-center">
              {language === 'es' ? 'Esto tomará unos segundos.' : 'This will only take a few seconds.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (failed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/20 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 flex flex-col items-center gap-4 text-center">
            <AlertCircle className="w-14 h-14 text-amber-500" />
            <p className="text-xl font-semibold">
              {language === 'es' ? 'No pudimos confirmar el pago' : "We couldn't confirm your payment"}
            </p>
            <p className="text-sm text-muted-foreground">
              {language === 'es'
                ? 'Si se te cobró, tu plan se activará en breve. Puedes reintentar o revisar tu suscripción.'
                : "If you were charged, your plan will activate shortly. You can retry or check your subscription."}
            </p>
            <div className="flex gap-3 pt-2">
              <Button onClick={() => navigate('/pricing')} variant="outline">
                {language === 'es' ? 'Volver a Planes' : 'Back to Plans'}
              </Button>
              <Button onClick={loadSubscription}>
                {language === 'es' ? 'Reintentar' : 'Re-check'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/20 p-4">
      {showConfetti && <Confetti />}
      
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <div className="relative">
              <CheckCircle className="w-24 h-24 text-green-500" />
              <Sparkles className="w-8 h-8 text-yellow-500 absolute -top-2 -right-2 animate-pulse" />
            </div>
          </div>
          <CardTitle className="text-3xl">
            {language === 'es' ? '🎉 ¡Pago Exitoso!' : '🎉 Payment Successful!'}
          </CardTitle>
        </CardHeader>
        
        <CardContent className="space-y-6">
          <div className="text-center">
            <p className="text-lg text-muted-foreground">
              {language === 'es'
                ? 'Tu suscripción ha sido activada con éxito'
                : 'Your subscription has been successfully activated'}
            </p>
          </div>
          
          {subscription && (
            <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 p-6 rounded-lg border border-purple-200 dark:border-purple-800">
              <h3 className="font-semibold text-lg mb-4">
                {language === 'es' ? 'Detalles de la Suscripción' : 'Subscription Details'}
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{language === 'es' ? 'Plan' : 'Plan'}:</span>
                  <span className="font-semibold">{subscription.plan_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{language === 'es' ? 'Monto' : 'Amount'}:</span>
                  <span className="font-semibold">${subscription.amount_paid}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{language === 'es' ? 'Estado' : 'Status'}:</span>
                  <span className="font-semibold text-green-600">
                    {language === 'es' ? 'Activa' : 'Active'}
                  </span>
                </div>
                {subscription.expires_at && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{language === 'es' ? 'Válido hasta' : 'Valid until'}:</span>
                    <span className="font-semibold">
                      {new Date(subscription.expires_at).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Premium tools tour */}
          <div>
            <h3 className="font-semibold mb-1 text-center">
              {language === 'es' ? '✨ Tus herramientas Premium están desbloqueadas' : '✨ Your Premium tools are unlocked'}
            </h3>
            <p className="text-sm text-muted-foreground text-center mb-4">
              {language === 'es' ? 'Toca cualquiera para comenzar tu recorrido' : 'Tap any one to start your tour'}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                { icon: MessageSquare, route: '/ai-insights', en: 'AI Insights', es: 'Perspectivas IA', c: 'text-purple-600' },
                { icon: BookOpen, route: '/commentary', en: 'Commentaries', es: 'Comentarios', c: 'text-blue-600' },
                { icon: Languages, route: '/original-languages', en: 'Original Languages', es: 'Idiomas Originales', c: 'text-emerald-600' },
                { icon: Search, route: '/word-study', en: 'Word Study', es: 'Estudio de Palabras', c: 'text-amber-600' },
                { icon: Map, route: '/maps', en: '3D Bible Maps', es: 'Mapas 3D', c: 'text-rose-600' },
                { icon: ScrollText, route: '/dashboard', en: 'Study Dashboard', es: 'Panel de Estudio', c: 'text-indigo-600' },
              ].map((t) => (
                <button
                  key={t.route}
                  onClick={() => navigate(t.route)}
                  data-testid={`unlock-tour-${t.route.replace('/', '')}`}
                  className="group flex flex-col items-center gap-2 p-4 rounded-xl border border-border bg-card hover:border-purple-400 hover:shadow-md transition-all text-center"
                >
                  <t.icon className={`w-6 h-6 ${t.c} group-hover:scale-110 transition-transform`} />
                  <span className="text-xs font-medium">{language === 'es' ? t.es : t.en}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={() => navigate('/dashboard')}
              className="flex-1"
              size="lg"
            >
              {language === 'es' ? 'Ir al Dashboard' : 'Go to Dashboard'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              onClick={() => navigate('/bible')}
              variant="outline"
              className="flex-1"
              size="lg"
            >
              {language === 'es' ? 'Comenzar a Leer' : 'Start Reading'}
            </Button>
          </div>
          
          {/* Support */}
          <div className="text-center text-sm text-muted-foreground">
            {language === 'es'
              ? 'Un email de confirmación ha sido enviado a tu correo'
              : 'A confirmation email has been sent to your inbox'}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccessPage;